import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';
import { AppLayoutRoute } from './routes/app-layout';
import { NotFoundPage } from '@/features/errors/not-found-page';
import { LoginRoute } from './routes/login';
import { loginSearchSchema } from './routes/login-search';
import { ensureSession } from '@/lib/api/client';
import { meQuery, navigationQuery } from './session/queries';
import { isPlatformSession } from './shell/navigation';
import { PageSkeleton } from './page-skeleton';
import { RouteError } from './route-error';
import { Splash } from './splash';
import { parsePipelineSearch } from '@/features/pipeline/board-filters';

// Feature screens are code-split per route.
const platformPages = () => import('./routes/platform');
const adminPages = () => import('./routes/admin');
const setupPages = () => import('./routes/setup');
const workPages = () => import('./routes/work');

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: () => <NotFoundPage fullscreen />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: loginSearchSchema,
  component: LoginRoute,
});

/** Authenticated layout: restores the session, prefetches /me + navigation, renders the shell. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ location }) => {
    const ok = await ensureSession();
    if (!ok) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(meQuery),
      context.queryClient.ensureQueryData(navigationQuery),
    ]);
  },
  pendingComponent: Splash,
  errorComponent: RouteError,
  component: AppLayoutRoute,
});

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/features/home/home-page'), 'HomePage'),
});

/** Platform (Super Admin) section — only for platform sessions. */
const platformRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform',
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!isPlatformSession(me)) throw redirect({ to: '/' });
  },
  component: Outlet,
});
const tenantsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/tenants',
  component: lazyRouteComponent(platformPages, 'PlatformTenantsRoute'),
});
const tenantDetailRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/tenants/$tenantId',
  component: lazyRouteComponent(platformPages, 'PlatformTenantDetailRoute'),
});
const plansRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/plans',
  component: lazyRouteComponent(platformPages, 'PlatformPlansRoute'),
});
const modulesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/modules',
  component: lazyRouteComponent(platformPages, 'PlatformModulesRoute'),
});

const jobsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/jobs',
  component: lazyRouteComponent(platformPages, 'PlatformJobsRoute'),
});

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account',
  component: lazyRouteComponent(() => import('@/features/account/account-page'), 'AccountPage'),
});

const adminUsersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/users',
  component: lazyRouteComponent(adminPages, 'AdminUsersRoute'),
});
const adminAuditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/audit',
  component: lazyRouteComponent(adminPages, 'AdminAuditRoute'),
});
const adminSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/settings',
  component: lazyRouteComponent(adminPages, 'AdminSettingsRoute'),
});

// P3 core
const pipelineRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/pipeline',
  validateSearch: parsePipelineSearch,
  component: lazyRouteComponent(workPages, 'PipelineRoute'),
});
const accountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/accounts',
  component: lazyRouteComponent(workPages, 'AccountsRoute'),
});
const accountDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/accounts/$accountId',
  component: lazyRouteComponent(workPages, 'AccountDetailRoute'),
});
const targetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/setup/targets',
  component: lazyRouteComponent(workPages, 'TargetsRoute'),
});
const playbookRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/setup/playbook',
  component: lazyRouteComponent(setupPages, 'SetupPlaybookRoute'),
});
const icpsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/setup/icps',
  component: lazyRouteComponent(setupPages, 'SetupIcpsRoute'),
});
const listsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/setup/lists',
  component: lazyRouteComponent(setupPages, 'SetupListsRoute'),
});
const libraryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/setup/library',
  component: lazyRouteComponent(setupPages, 'SetupLibraryRoute'),
});
const importRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/setup/import',
  component: lazyRouteComponent(setupPages, 'SetupImportRoute'),
});

/** Any other in-app path: nav items not built yet render "Coming soon"; unknown paths 404. */
const catchAllRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '$',
  component: lazyRouteComponent(() => import('@/features/coming-soon/coming-soon-page'), 'ComingSoonPage'),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    homeRoute,
    platformRoute.addChildren([tenantsRoute, tenantDetailRoute, plansRoute, modulesRoute, jobsRoute]),
    accountRoute,
    adminUsersRoute,
    adminAuditRoute,
    adminSettingsRoute,
    pipelineRoute,
    accountsRoute,
    accountDetailRoute,
    targetsRoute,
    playbookRoute,
    icpsRoute,
    listsRoute,
    libraryRoute,
    importRoute,
    catchAllRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    ...(history ? { history } : {}),
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultPendingComponent: PageSkeleton,
    defaultPendingMs: 200,
    defaultPendingMinMs: 300,
    defaultErrorComponent: RouteError,
    scrollRestoration: true,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
